import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
  useDocumentMeta({
    title: "Services · Program Resilience, Event OS, AI Readiness — JJ Argent",
    description:
      "Five senior services: Program Resilience Audit, Event Operating System Build, Workflow & AI Readiness Review, Program & Experience Design, Interim Leadership.",
    path: "/services",
  });
  const [SERVICES, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/services`).then((r) => {
      setServices(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid="services-page">
      {/* hero */}
      <section className="pt-40 pb-20 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="eyebrow">Services</p>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em] max-w-4xl">
            How I help organisations{" "}<br />
            <span className="text-[#D8A936]">build work that lasts.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#A9A9A6]">
            I work with complex organisations — universities, festivals, hospitality, corporate operations,
            community groups and government teams — to improve the systems beneath their programs.
            Every engagement is scoped to your complexity, timeline and requirements.
          </p>
        </div>
      </section>

      {/* Service list */}
      <section className="border-t border-[#343437]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {loading && <div className="text-[#A9A9A6] py-8" data-testid="services-loading">Loading services…</div>}
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              initial={{ y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.05 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-20 lg:py-28 border-b border-[#343437] last:border-b-0"
              data-testid={`service-${s.id}`}
            >
              <div className="lg:col-span-4">
                <div className="flex items-baseline gap-6">
                  <span className="oversize text-[80px] md:text-[110px] text-[#D8A936] leading-none">{s.n}</span>
                </div>
                <h2 className="mt-6 font-display text-3xl md:text-4xl leading-tight tracking-tight text-[#FAF9F6]">
                  {s.title}
                </h2>
              </div>

              <div className="lg:col-span-5">
                <p className="text-lg leading-relaxed text-[#A9A9A6]">{s.lead}</p>
                <p className="mt-6 eyebrow">Deliverables</p>
                <ul className="mt-4 space-y-3">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-[#FAF9F6]">
                      <Check size={16} className="text-[#D8A936] mt-1 shrink-0" />
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-3">
                <div className="border border-[#343437] p-6 bg-[#151517]">
                  <p className="eyebrow">Pricing</p>
                  {/* TODO(JJ): Replace this placeholder with real pricing signal
                      (e.g. "from $X" or "$X – $Y" or a typical engagement length).
                      All four services currently render the same generic line;
                      the batch brief flags this as required before publishing. */}
                  <p
                    className="mt-3 text-[#FAF9F6] leading-relaxed"
                    data-testid={`service-pricing-${s.id}`}
                    data-todo="jj-pricing"
                  >
                    Scoped to your complexity, timeline and requirements.
                    <span className="block mt-2 text-[10px] tracking-[0.18em] uppercase text-[#D8A936]/70">
                      Pricing signal — pending
                    </span>
                  </p>
                  <Link
                    to="/contact"
                    data-testid={`service-cta-${s.id}`}
                    className="btn-primary mt-6 w-full justify-center"
                  >
                    {s.cta} <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Closing */}
      <section className="bg-[#F3EFE6] text-[#0B0B0C] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow !text-[#B98B24]">Not sure which fits?</p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Start with a 30-minute discovery call.
            </h2>
            <p className="mt-6 text-[#343437] max-w-2xl leading-relaxed">
              Or take the 2-minute Program Resilience diagnostic first — you'll get a tier, a short read
              on where you sit, and three actions to start on this week.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/diagnostic" data-testid="services-diagnostic" className="btn-ghost-dark">
              Take the diagnostic <ArrowUpRight size={14} />
            </Link>
            <Link to="/contact" data-testid="services-book-call" className="btn-ghost-dark">
              Book a call <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
